import { useState, useEffect, useRef } from 'react';
import { 
  DownloadProgressInfo, 
  UseDownloadProgressProviderReturn 
} from './types';

/**
 * DownloadProgressProviderのロジックを管理するカスタムフック
 * ダウンロード進捗の表示・非表示の制御と複数ダウンロードの管理を行う
 */
export const useDownloadProgressProvider = (): UseDownloadProgressProviderReturn => {
  // 複数のダウンロード進捗を管理するための配列
  const [downloadProgresses, setDownloadProgresses] = useState<DownloadProgressInfo[]>([]);
  
  // 自動クローズ用にタイマーを保持
  const progressTimers = useRef<Record<string, NodeJS.Timeout>>({});
  
  // IDの一意性を確保するためのカウンター
  const idCounter = useRef<number>(0);

  /**
   * ダウンロード進捗を開始する関数
   * @param id ダウンロードの一意ID
   * @param fileName ファイル名
   * @param total 総バイト数
   */
  const startDownloadProgress = (
    id: string,
    fileName: string,
    total: number
  ): void => {
    // 新しいダウンロード進捗を追加
    const newProgress: DownloadProgressInfo = {
      id,
      fileName,
      progress: 0,
      loaded: 0,
      total,
      open: true
    };
    
    // 表示中のダウンロード進捗が5個を超えないように制限
    setDownloadProgresses(prev => {
      const updatedProgresses = [...prev, newProgress];
      if (updatedProgresses.length > 5) {
        // 最古の進捗を削除
        const oldestProgress = updatedProgresses[0];
        if (progressTimers.current[oldestProgress.id]) {
          clearTimeout(progressTimers.current[oldestProgress.id]);
          delete progressTimers.current[oldestProgress.id];
        }
        return updatedProgresses.slice(1);
      }
      return updatedProgresses;
    });
  };

  /**
   * ダウンロード進捗を更新する関数
   * @param id ダウンロードの一意ID
   * @param loaded ダウンロード済みバイト数
   * @param total 総バイト数
   */
  const updateDownloadProgress = (
    id: string,
    loaded: number,
    total: number
  ): void => {
    setDownloadProgresses(prev =>
      prev.map(progress => {
        if (progress.id === id) {
          const newProgress = Math.min(100, (loaded / total) * 100);
          return {
            ...progress,
            loaded,
            total,
            progress: newProgress
          };
        }
        return progress;
      })
    );
  };

  /**
   * ダウンロード進捗を完了する関数
   * @param id ダウンロードの一意ID
   */
  const completeDownloadProgress = (id: string): void => {
    // 進捗を100%に設定
    setDownloadProgresses(prev =>
      prev.map(progress => {
        if (progress.id === id) {
          return {
            ...progress,
            progress: 100,
            loaded: progress.total
          };
        }
        return progress;
      })
    );

    // 3秒後に自動的に閉じる
    progressTimers.current[id] = setTimeout(() => {
      closeDownloadProgress(id);
    }, 3000);
  };

  /**
   * ダウンロード進捗を閉じる関数
   * @param id ダウンロードの一意ID
   */
  const closeDownloadProgress = (id: string): void => {
    setDownloadProgresses(prev =>
      prev.map(progress =>
        progress.id === id ? { ...progress, open: false } : progress
      )
    );

    // 閉じた進捗を配列から削除するタイマー
    setTimeout(() => {
      setDownloadProgresses(prev => prev.filter(progress => progress.id !== id));

      // タイマーの参照も削除
      if (progressTimers.current[id]) {
        clearTimeout(progressTimers.current[id]);
        delete progressTimers.current[id];
      }
    }, 500); // アニメーション完了後に削除
  };

  // コンポーネントがアンマウントする際にタイマーをクリア
  useEffect(() => {
    return () => {
      Object.values(progressTimers.current).forEach(timer => {
        clearTimeout(timer);
      });
    };
  }, []);

  return {
    startDownloadProgress,
    updateDownloadProgress,
    completeDownloadProgress,
    closeDownloadProgress,
    downloadProgresses
  };
};



